import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import { Suspense, useState } from "react";
import { DismissTaskDialog } from "@/components/dismiss-task-dialog";
import { PageHeader } from "@/components/page-header";
import { LoadingIndicator, PageLoading } from "@/components/page-loading";
import { PageShell } from "@/components/page-shell";
import { Button } from "@/components/ui/button";
import { FormError } from "@/components/ui/form-error";
import {
  type Task,
  tasksQueryOptions,
  useReissueTaskMutation,
} from "@/lib/data";
import { requireSession } from "@/lib/session";
import { getTaskDefinition } from "@/lib/task-definitions";
import {
  ensureTask,
  getTaskStatusLabel,
  isClosedTask,
  isSkippedTask,
  useAdvanceFromTask,
} from "@/lib/task-progress";
import { handleApiError } from "@/lib/utils";

export const Route = createFileRoute("/steps_/$taskId")({
  component: TaskDetailPage,
  beforeLoad: requireSession,
  loader: async ({ params }) => {
    const task = await ensureTask(params.taskId);
    if (!task) {
      throw redirect({ to: "/steps" });
    }
    return { task };
  },
});

function TaskDetailPage() {
  const { taskId } = Route.useParams();
  const { data, isLoading } = useQuery(tasksQueryOptions);

  if (isLoading) {
    return <PageLoading />;
  }

  const task = data?.tasks.find((t) => t.id === taskId);
  if (!task) {
    return null;
  }

  const taskDefinition = getTaskDefinition(task.content_type);
  const Icon = taskDefinition.icon;
  const closed = isClosedTask(task);

  return (
    <PageShell>
      <PageHeader
        eyebrow={
          <span className="inline-flex items-center gap-1.5">
            <Icon className="size-3.5" />
            {getTaskStatusLabel(task.status)}
          </span>
        }
        title={task.title}
        description={task.description}
      />

      {closed ? <CompletedView task={task} /> : <ActiveView task={task} />}
    </PageShell>
  );
}

function ActiveView({ task }: { task: Task }) {
  const taskDefinition = getTaskDefinition(task.content_type);
  const onComplete = useAdvanceFromTask(task.id, {
    repeatsUntilClosed: taskDefinition.repeatsUntilClosed,
  });
  const [dismissOpen, setDismissOpen] = useState(false);
  const TaskForm = taskDefinition.formComponent;

  return (
    <Suspense fallback={<LoadingIndicator />}>
      <TaskForm key={task.id} task={task} onComplete={onComplete} />
      {task.dismissible && (
        <>
          <div className="flex justify-center pt-3">
            <button
              type="button"
              onClick={() => setDismissOpen(true)}
              className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              I can't complete this step
            </button>
          </div>
          <DismissTaskDialog
            task={task}
            open={dismissOpen}
            onOpenChange={setDismissOpen}
            onDismissed={onComplete}
          />
        </>
      )}
    </Suspense>
  );
}

function CompletedView({ task }: { task: Task }) {
  const navigate = useNavigate();
  const reissue = useReissueTaskMutation();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const taskDefinition = getTaskDefinition(task.content_type);
  const TaskSummary = taskDefinition.summaryComponent;
  // Only Closed tasks reach here, so not skipped means completed. A skipped
  // task has nothing to edit; it comes back through reissue instead.
  const canEdit = taskDefinition.editable && !isSkippedTask(task);

  if (editing) {
    return <EditView task={task} onDone={() => setEditing(false)} />;
  }

  const handleUnskip = async () => {
    setError(null);
    try {
      const result = await reissue.mutateAsync(task.id);
      void navigate({ to: "/steps/$taskId", params: { taskId: result.id } });
    } catch (err) {
      setError(await handleApiError(err));
    }
  };

  return (
    <div className="space-y-5">
      <Suspense fallback={<LoadingIndicator />}>
        <TaskSummary task={task} />
      </Suspense>
      {isSkippedTask(task) && (
        <>
          <FormError message={error} />
          <Button
            type="button"
            size="lg"
            className="w-full"
            onClick={handleUnskip}
            disabled={reissue.isPending}
          >
            {reissue.isPending
              ? "Bringing it back…"
              : "I want to do this step after all"}
          </Button>
        </>
      )}
      {canEdit && (
        <Button
          type="button"
          size="lg"
          className="w-full"
          onClick={() => setEditing(true)}
        >
          <Pencil className="size-4" />
          Edit
        </Button>
      )}
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full"
        onClick={() => navigate({ to: "/steps" })}
      >
        Back to your steps
      </Button>
    </div>
  );
}

function EditView({ task, onDone }: { task: Task; onDone: () => void }) {
  const TaskForm = getTaskDefinition(task.content_type).formComponent;

  return (
    <Suspense fallback={<LoadingIndicator />}>
      <div className="space-y-5">
        {/* The task is already complete, so saving returns to the summary
            instead of advancing to the next task. */}
        <TaskForm key={task.id} task={task} onComplete={async () => onDone()} />
        <Button
          type="button"
          variant="ghost"
          size="lg"
          className="w-full"
          onClick={onDone}
        >
          Cancel
        </Button>
      </div>
    </Suspense>
  );
}
